// src/pages/Cache.js
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { apiGet } from "../api/api";

const DataContext = createContext(null);

const safeArr = (v) => (Array.isArray(v) ? v : []);

const pickFirstArray = (sources = []) => {
  for (const source of sources) {
    const arr = safeArr(source);
    if (arr.length) return arr;
  }
  return [];
};

const mergeById = (sources = [], idKey = "id") => {
  const map = new Map();
  sources.forEach((source) => {
    safeArr(source).forEach((item) => {
      if (!item) return;
      const id = item[idKey] ?? item.id;
      if (id != null) {
        const key = String(id);
        if (!map.has(key)) {
          map.set(key, item);
        } else {
          map.set(key, { ...item, ...map.get(key) });
        }
      }
    });
  });
  return Array.from(map.values());
};

const mergePlayerStats = (sources = []) => {
  const map = new Map();
  sources.forEach((source) => {
    safeArr(source).forEach((s) => {
      if (!s) return;
      const pid = s?.player_id ?? s?.playerId ?? s?.oyuncu_id ?? s?.oyuncu?.id ?? s?.player?.id;
      const mid = s?.match_id ?? s?.matchId ?? s?.match?.id;
      if (pid != null && mid != null) {
        const key = `${pid}:${mid}`;
        if (!map.has(key)) {
          map.set(key, s);
        } else {
          map.set(key, { ...s, ...map.get(key) });
        }
      }
    });
  });
  return Array.from(map.values());
};

const extractStandings = (payload) => {
  const s1 = safeArr(payload?.standings);
  const s2 = safeArr(payload?.data?.standings);
  const s3 = safeArr(payload);
  return s1.length ? s1 : s2.length ? s2 : s3;
};

const toScopedId = (value) => String(value ?? "").trim();

const rowMatchesScope = (row, cityId, leagueId, seasonId) => {
  if (!row) return false;

  const matchesField = (keys, expected) => {
    const target = toScopedId(expected);
    if (!target) return true;

    const candidates = keys
      .map((key) => row?.[key])
      .map((value) => toScopedId(value))
      .filter(Boolean);

    if (!candidates.length) return false;
    return candidates.includes(target);
  };

  return (
    matchesField(["city_id", "cityId", "city"], cityId) &&
    matchesField(["league_id", "leagueId", "league"], leagueId) &&
    matchesField(["season_id", "seasonId", "season", "match_season"], seasonId)
  );
};

const defaultData = {
  cities: [],
  matches: [],
  teams: [],
  players: [],
  playerStatistics1: [],
  macEvents: [],
  macKadrolari: [],
  standings: [],
};

export const DataProvider = ({ children }) => {
  const [data, setData] = useState(defaultData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const lastScopeKeyRef = useRef("");

  // META
  const refetchMeta = useCallback(async () => {
    try {
      setError("");
      const res = await apiGet("/api/meta/cities", { cacheTtl: 5 * 60 * 1000 });
      const cities = safeArr(res?.data?.cities).length
        ? safeArr(res?.data?.cities)
        : safeArr(res?.data);

      setData((prev) => ({ ...prev, cities }));
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Şehirler alınamadı.");
    }
  }, []);

  // SCOPE (✅ silent param eklendi)
  const refetchScope = useCallback(
    async ({
      cityId,
      leagueId,
      seasonId,
      limit = 300,
      offset = 0,
      silent = false, // ✅
    } = {}) => {
      const c = String(cityId ?? "").trim();
      const l = String(leagueId ?? "").trim();
      const s = String(seasonId ?? "").trim();
      if (!c || !l || !s) return;

      const scopeKey = `${c}-${l}-${s}-${limit}-${offset}`;
      lastScopeKeyRef.current = scopeKey;

      try {
        if (!silent) setLoading(true);
        setError("");

        // Üç istek de birbirinden bağımsız; ART ARDA değil AYNI ANDA gönderilir.
        // Önceden overview bitmeden /maclar, o bitmeden /puan-durumu
        // başlamıyordu: kullanıcı üç gidiş-dönüşü üst üste bekliyordu. Biri
        // başarısız olursa diğerleri yine sonuç verebilsin diye allSettled.
        const [overviewResult, legacyMatchesResult, standingsResult] = await Promise.allSettled([
          apiGet("/api/home/overview", {
            params: {
              cityId: c,
              leagueId: l,
              seasonId: s,
              limit,
              offset,
              include: "matches,teams,players,playerStats,standings,events,lineups",
            },
            cacheTtl: silent ? 20000 : 10000,
          }),
          // Lig/sezon sunucuda süzülür; sonuç yine aşağıda kapsamla süzülür.
          apiGet("/maclar", { params: { league_id: l, season_id: s }, cacheTtl: 20000 }),
          apiGet("/puan-durumu", {
            params: { cityId: c, leagueId: l, seasonId: s },
            cacheTtl: 30000,
          }),
        ]);

        if (overviewResult.status === "rejected") throw overviewResult.reason;

        const payload = overviewResult.value?.data;
        const overviewMatches = mergeById([
          payload?.matches,
          payload?.data?.matches,
          payload?.fixtures,
          payload?.data?.fixtures,
        ]);

        let legacyMatches = [];
        if (legacyMatchesResult.status === "fulfilled") {
          legacyMatches = mergeById([legacyMatchesResult.value?.data]).filter((match) =>
            rowMatchesScope(match, c, l, s)
          );
        } else {
          const legacyMatchError = legacyMatchesResult.reason;
          console.warn(
            "Legacy matches fetch failed, using overview matches only:",
            legacyMatchError?.response?.data || legacyMatchError?.message
          );
        }

        const matches = mergeById([overviewMatches, legacyMatches]);
        const teams = mergeById([payload?.teams, payload?.data?.teams]);
        const players = mergeById([
          payload?.players,
          payload?.player_list,
          payload?.data?.players,
          payload?.data?.player_list,
        ]);
        const playerStats = mergePlayerStats([
          // Yeni birleşik response shape'inde playerStatistics de gelebilir.
          payload?.playerStatistics,
          payload?.playerStats,
          payload?.player_stats,
          payload?.playerStatistics1,
          payload?.player_statistics,
          payload?.data?.playerStatistics,
          payload?.data?.playerStats,
          payload?.data?.player_stats,
          payload?.data?.playerStatistics1,
          payload?.data?.player_statistics,
        ]);
        const baseMacEvents = pickFirstArray([
          payload?.macEvents,
          payload?.mac_olaylari,
          payload?.events,
          payload?.data?.macEvents,
          payload?.data?.mac_olaylari,
          payload?.data?.events,
        ]);
        const eskiMacEvents = safeArr(payload?.eskiMacOlaylari)
          .concat(safeArr(payload?.eski_mac_olaylari))
          .concat(safeArr(payload?.data?.eskiMacOlaylari))
          .concat(safeArr(payload?.data?.eski_mac_olaylari))
          .map((e) => ({ ...e, source_table: e?.source_table || "eski_mac_olaylari" }));
        const macEvents = eskiMacEvents.length
          ? mergeById([baseMacEvents, eskiMacEvents])
          : baseMacEvents;

        const macKadrolari = pickFirstArray([
          payload?.macKadrolari,
          payload?.mac_kadrolari,
          payload?.lineups,
          payload?.data?.macKadrolari,
          payload?.data?.mac_kadrolari,
          payload?.data?.lineups,
        ]);

        const overviewStandings = extractStandings(payload);
        let standings = overviewStandings;

        if (standingsResult.status === "fulfilled") {
          const scopedStandings = extractStandings(standingsResult.value?.data);
          if (scopedStandings.length) {
            standings = scopedStandings;
          }
        } else {
          const e = standingsResult.reason;
          console.warn(
            "Standings scoped fetch failed, using overview standings:",
            e?.response?.data || e?.message
          );
        }

        if (lastScopeKeyRef.current !== scopeKey) return;

        setData((prev) => ({
          ...prev,
          matches,
          teams,
          players,
          playerStatistics1: playerStats,
          macEvents,
          macKadrolari,
          standings,
        }));
      } catch (e) {
        console.error(e);
        if (lastScopeKeyRef.current !== scopeKey) return;
        if (!silent) setError(e?.response?.data?.error || "Veriler alınamadı.");
      } finally {
        if (!silent && lastScopeKeyRef.current === scopeKey) setLoading(false);
      }
    },
    []
  );

  const value = useMemo(
    () => ({
      ...data,
      loading,
      error,
      refetchMeta,
      refetchScope,
    }),
    [data, loading, error, refetchMeta, refetchScope]
  );

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = () => useContext(DataContext);
